import { Badge, Typography, Card, CardContent, CardActionArea, Divider, Stack } from "@mui/material";
import { InlineProgress } from "@gui/widgets/InlineProgress";
import Grid from "@mui/material/Grid2";
import { ISSUE_COUNT_LOADING_LABEL, issueCountBadgeLabel } from "@gui/contexts/issueCounts/IssueCountsContext";
import { ICON_COUNT_BADGE_SX } from "@gui/widgets/Toolbar";
import { VisuallyHidden } from "@gui/widgets/A11y";
import { PageLayout } from "@gui/widgets/PageLayout";
import { useHomeViewModel, type HomeCard } from "./Home.vm";

/**
 * Stable E2E hook for a landing-grid card, derived from its label so a spec can name a card
 * the way a user sees it: "Company Settings" -> `home-card-company-settings`.
 *
 * The card labels come from the route registry (`routes/routeTable.tsx`) and are unique across its links,
 * so the ids are unique too. This is what lets `e2e/lib/home.ts` stop disambiguating cards
 * from the sidebar's identically-labeled entries by `<h6>`-vs-`<span>` DOM shape.
 */
function homeCardTestId(label: string): string {
	return `home-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

/** The card's icon, with its count badge when it has one. The badge is aria-hidden and its
 *  meaning re-stated as text on the card. Left exposed, the spinner contributes nothing at all and
 *  the count contributes a bare number ("Job Errors 6") that never says what is being counted. */
function CardIcon(props: { card: HomeCard }) {
	if (props.card.countLoading) {
		return (
			<Badge
				badgeContent={<InlineProgress size="badge" color="inherit" />}
				color="error"
				sx={ICON_COUNT_BADGE_SX}
				aria-hidden="true"
			>
				{props.card.icon}
			</Badge>
		);
	}
	if (props.card.count !== undefined) {
		return (
			<Badge badgeContent={props.card.count} color="error" sx={ICON_COUNT_BADGE_SX} aria-hidden="true">
				{props.card.icon}
			</Badge>
		);
	}
	return props.card.icon;
}

/** Home: a card for each page the user can open, one grid per sidebar section. */
export function HomePage() {
	const vm = useHomeViewModel();

	return (
		<PageLayout>
			<Stack spacing={2} divider={<Divider />}>
				{vm.sections.map((cards) => (
					<Grid key={cards[0].to} container spacing={2}>
						{cards.map((card) => (
							<Grid key={card.to} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
								<Card className="home-card" data-testid={homeCardTestId(card.text)}>
									<CardActionArea onClick={() => vm.open(card.to)} aria-busy={card.countLoading}>
										{/* gap 2 (16px) clears a two-digit count badge hanging off the icon's corner. */}
										<CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
											<CardIcon card={card} />
											<Typography variant="h6">{card.text}</Typography>
											{card.countLoading && <VisuallyHidden>{ISSUE_COUNT_LOADING_LABEL}</VisuallyHidden>}
											{card.count !== undefined && <VisuallyHidden>{issueCountBadgeLabel(card.count)}</VisuallyHidden>}
										</CardContent>
									</CardActionArea>
								</Card>
							</Grid>
						))}
					</Grid>
				))}
			</Stack>
		</PageLayout>
	);
}
